import { createResource, type RouteResource } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags';
import { eocCommonResources, type UserResourceType } from '@atlassian/eoc-resources';
import {
	getTimeTemplatesResource as sharedScheduleTimeTemplatesResource,
	type GetTimezonesResponse,
	resources as scheduleCommonResources,
} from '@atlassian/eoc-schedule-common/resources';
import type { GetTimeTemplatesResponse as ScheduleTimeTemplatesResponse } from '@atlassian/eoc-schedule-common/types/time';

import { getTeamIdFromContext } from '../../utils';
import { endpoints as editEndpoints } from '../endpoints/edit';
import { endpoints as listEndpoints } from '../endpoints/list';
import type { TeamScheduleListResponse } from '../endpoints/list/types';
import { endpoints as pageEndpoints } from '../endpoints/page';
import type { UiPreferencesResponse } from '../endpoints/page/types';
import { endpoints as sectionEndpoints } from '../endpoints/section';
import type { ScheduleRightsResponse } from '../endpoints/section/types';

const getSchedulesResource: RouteResource<TeamScheduleListResponse> = createResource({
	type: 'getSchedulesResource',
	getKey: () => 'getSchedulesResource',
	isBrowserOnly: true,
	getData: async (routerExtendedContext) =>
		listEndpoints.getScheduleList(getTeamIdFromContext(routerExtendedContext)),
});

const getUiPreferencesResource: RouteResource<UiPreferencesResponse> = createResource({
	type: 'getSchedulePreferencesResource',
	getKey: () => 'getSchedulePreferencesResource',
	isBrowserOnly: true,
	getData: async (routerExtendedContext) =>
		pageEndpoints.getUiPreferences(getTeamIdFromContext(routerExtendedContext)),
});

const getScheduleRightsResource: RouteResource<ScheduleRightsResponse> = createResource({
	type: 'getScheduleRightsResource',
	getKey: () => 'getScheduleRightsResource',
	isBrowserOnly: true,
	getData: async (routerExtendedContext) =>
		sectionEndpoints.getScheduleRights(getTeamIdFromContext(routerExtendedContext)),
});

const localScheduleTimeTemplatesResource: RouteResource<ScheduleTimeTemplatesResponse> =
	createResource({
		type: 'getScheduleTimeTemplatesResource',
		getKey: () => 'getScheduleTimeTemplatesResourceurce',
		isBrowserOnly: true,
		getData: async () => editEndpoints.getScheduleTimeTemplates(),
	});

const getScheduleTimeTemplatesResource = (): RouteResource<ScheduleTimeTemplatesResponse> =>
	fg('alx_schedule_deduplication')
		? sharedScheduleTimeTemplatesResource
		: localScheduleTimeTemplatesResource;

const resources = (): (
	| RouteResource<TeamScheduleListResponse>
	| RouteResource<UiPreferencesResponse>
	| RouteResource<ScheduleRightsResponse>
	| RouteResource<ScheduleTimeTemplatesResponse>
	| RouteResource<GetTimezonesResponse>
	| RouteResource<UserResourceType>
)[] => [
	getSchedulesResource,
	getUiPreferencesResource,
	getScheduleRightsResource,
	...(fg('alx_schedule_deduplication') ? [] : [localScheduleTimeTemplatesResource]),
	...scheduleCommonResources(),
	...eocCommonResources(),
];

export type {
	TeamScheduleListResponse,
	UiPreferencesResponse,
	ScheduleRightsResponse,
	ScheduleTimeTemplatesResponse,
};

export {
	getSchedulesResource,
	getUiPreferencesResource,
	getScheduleRightsResource,
	getScheduleTimeTemplatesResource,
	resources,
};
